// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Funnel
 * @package @geenius/analytics
 * @description Shared funnel-analysis helpers used by framework hooks,
 * primitives, and backend consumers to derive conversion performance from raw
 * analytics events.
 */
import type { AnalyticsEvent } from "./index";
import type { FunnelResult, FunnelStep, FunnelStepResult } from "./types";

function matchesStep(event: AnalyticsEvent, step: FunnelStep): boolean {
  if (event.name !== step.event) return false;
  if (step.properties) {
    return Object.entries(step.properties).every(
      ([key, value]) => event.properties?.[key] === value,
    );
  }
  return true;
}

function getFunnelIdentity(event: AnalyticsEvent): string {
  return event.userId ?? event.sessionId;
}

function average(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/**
 * Analyzes analytics events against an ordered funnel definition.
 *
 * @param events Analytics events to evaluate against the supplied steps.
 * @param steps Ordered funnel definition describing the expected conversion path.
 * @param name Human-readable funnel name used in the returned result.
 * @returns Funnel analysis data including step counts, drop-off, and conversion rates.
 */
export function analyzeFunnel(
  events: AnalyticsEvent[],
  steps: FunnelStep[],
  name = "Unnamed Funnel",
): FunnelResult {
  if (steps.length === 0)
    return {
      name,
      steps: [],
      totalEntries: 0,
      totalConversions: 0,
      overallConversionRate: 0,
      averageTimeToConvertMs: 0,
      analyzedAt: Date.now(),
    };

  const groupedEvents = new Map<string, AnalyticsEvent[]>();

  for (const event of events) {
    const identity = getFunnelIdentity(event);
    const identityEvents = groupedEvents.get(identity) ?? [];
    identityEvents.push(event);
    groupedEvents.set(identity, identityEvents);
  }

  const stepCounts = steps.map(() => 0);
  const stepTimings = steps.map((): number[] => []);
  const conversionTimings: number[] = [];

  for (const identityEvents of groupedEvents.values()) {
    const sortedEvents = [...identityEvents].sort(
      (left, right) => left.timestamp - right.timestamp,
    );
    const matchedTimestamps: number[] = [];
    let searchIndex = 0;

    for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
      const step = steps[stepIndex]!;
      const matchIndex = sortedEvents.findIndex(
        (event, eventIndex) =>
          eventIndex >= searchIndex && matchesStep(event, step),
      );

      if (matchIndex === -1) break;

      const matchedEvent = sortedEvents[matchIndex]!;
      stepCounts[stepIndex] = (stepCounts[stepIndex] ?? 0) + 1;
      matchedTimestamps.push(matchedEvent.timestamp);

      if (stepIndex > 0) {
        stepTimings[stepIndex]!.push(
          matchedEvent.timestamp - matchedTimestamps[stepIndex - 1]!,
        );
      }

      searchIndex = matchIndex + 1;
    }

    if (matchedTimestamps.length === steps.length) {
      conversionTimings.push(
        matchedTimestamps[matchedTimestamps.length - 1]! -
          matchedTimestamps[0]!,
      );
    }
  }

  const totalEntries = stepCounts[0] ?? 0;
  const stepResults: FunnelStepResult[] = steps.map((step, index) => {
    const count = stepCounts[index] ?? 0;
    const previousCount = index === 0 ? count : (stepCounts[index - 1] ?? 0);
    return {
      name: step.name,
      event: step.event,
      count,
      dropoffRate:
        index === 0
          ? 0
          : previousCount > 0
            ? ((previousCount - count) / previousCount) * 100
            : 100,
      conversionRate:
        index === 0 ? 100 : totalEntries > 0 ? (count / totalEntries) * 100 : 0,
      averageTimeFromPreviousMs: average(stepTimings[index]!),
    };
  });

  const totalConversions = stepResults[stepResults.length - 1]?.count ?? 0;
  const overallConversionRate =
    totalEntries > 0 ? (totalConversions / totalEntries) * 100 : 0;

  return {
    name,
    steps: stepResults,
    totalEntries,
    totalConversions,
    overallConversionRate,
    averageTimeToConvertMs: average(conversionTimings) ?? 0,
    analyzedAt: Date.now(),
  };
}

/**
 * Creates a typed funnel-step array from inline step definitions.
 *
 * @param steps Funnel-step definitions in evaluation order.
 * @returns The supplied steps as a typed funnel-step array.
 */
export function createFunnelSteps(
  ...steps: Array<{
    name: string;
    event: string;
    properties?: Record<string, unknown>;
  }>
): FunnelStep[] {
  return steps;
}
